import { useState, useCallback, useRef } from 'react';
import * as THREE from 'three';
import { apiFetch } from '../api';

// ============================================
// Patient Data Cache Utilities
// ============================================
const CACHE_PREFIX = 'neuronova_patient_data_';
const CACHE_VERSION = '2'; // Increment to invalidate all caches
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

const getCacheKey = (patientId) => {
    return `${CACHE_PREFIX}${CACHE_VERSION}_${patientId}`;
};

const getCachedPatientData = (patientId) => {
    try {
        const cacheKey = getCacheKey(patientId);
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);

        // Check if cache is expired
        if (Date.now() - timestamp > CACHE_EXPIRY_MS) {
            localStorage.removeItem(cacheKey);
            return null;
        }

        return data;
    } catch (error) {
        console.warn(`Error reading cache for patient ${patientId}:`, error);
        return null;
    }
};

const setCachedPatientData = (patientId, data) => {
    try {
        const cacheKey = getCacheKey(patientId);
        const cacheData = {
            data,
            timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
        // Handle quota exceeded error gracefully
        if (error.name === 'QuotaExceededError') {
            console.warn('localStorage quota exceeded, clearing old cache entries...');
            clearOldCacheEntries();
            // Try once more after clearing
            try {
                const cacheKey = getCacheKey(patientId);
                const cacheData = {
                    data,
                    timestamp: Date.now()
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (retryError) {
                console.warn(`Could not cache data for patient ${patientId}:`, retryError);
            }
        } else {
            console.warn(`Error caching data for patient ${patientId}:`, error);
        }
    }
};

const clearOldCacheEntries = () => {
    try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_PREFIX)) {
                try {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        const { timestamp } = JSON.parse(cached);
                        if (Date.now() - timestamp > CACHE_EXPIRY_MS) {
                            keysToRemove.push(key);
                        }
                    }
                } catch (e) {
                    // Invalid cache entry, remove it
                    keysToRemove.push(key);
                }
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`Cleared ${keysToRemove.length} old cache entries`);
    } catch (error) {
        console.warn('Error clearing old cache entries:', error);
    }
};

// Utility function to clear all patient data cache (exposed for manual cache management)
export const clearPatientDataCache = (patientId = null) => {
    try {
        if (patientId) {
            // Clear cache for specific patient
            const cacheKey = getCacheKey(patientId);
            localStorage.removeItem(cacheKey);
            console.log(`Cleared cache for patient ${patientId}`);
        } else {
            // Clear all patient data cache
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            console.log(`Cleared cache for ${keysToRemove.length} patients`);
        }
    } catch (error) {
        console.warn('Error clearing patient data cache:', error);
    }
};

const normalizePatientId = (patientId) => String(patientId || '').replace(/^sub-/, '');

const coordinateKey = (x, y, z) => (
    [x, y, z]
        .map(value => Number.parseFloat(value))
        .map(value => Number.isFinite(value) ? value.toFixed(1) : '')
        .join('|')
);

const contactKey = (value) => String(value || '').trim().toUpperCase();

const channelContacts = (channel) => (
    String(channel || '')
        .split(/[-_]/)
        .map(contactKey)
        .filter(Boolean)
);

const setZScoreLookupValue = (map, key, zScore) => {
    if (!key) {
        return;
    }

    const previous = map.get(key);
    if (previous === undefined || Math.abs(Number(zScore)) > Math.abs(Number(previous))) {
        map.set(key, zScore);
    }
};

const buildZScoreLookup = (patientZScoreData, feature) => {
    const lookup = {
        byChannelRegion: new Map(),
        byCoordinate: new Map()
    };

    if (!patientZScoreData?.regions || !feature) {
        return lookup;
    }

    patientZScoreData.regions.forEach(region => {
        (region.channels || []).forEach(channel => {
            const featureData = channel.z_scores?.[feature];
            const zScore = featureData?.z_score;
            if (zScore === null || zScore === undefined) {
                return;
            }

            const regionName = channel.roi || region.region_name || '';
            const channelName = channel.channel || '';
            setZScoreLookupValue(lookup.byChannelRegion, `${contactKey(channelName)}|${regionName}`, zScore);
            channelContacts(channelName).forEach(contact => {
                setZScoreLookupValue(lookup.byChannelRegion, `${contact}|${regionName}`, zScore);
            });

            const coords = channel.coordinates || {};
            [
                coordinateKey(coords.surfmm_x, coords.surfmm_y, coords.surfmm_z),
                coordinateKey(coords.mm_x, coords.mm_y, coords.mm_z)
            ].forEach(key => {
                if (key !== '||') {
                    setZScoreLookupValue(lookup.byCoordinate, key, zScore);
                }
            });
        });
    });

    return lookup;
};

const flattenLocalPatientZScorePoints = (patientZScoreData) => {
    if (!patientZScoreData?.regions) {
        return [];
    }

    const points = [];
    patientZScoreData.regions.forEach(region => {
        (region.channels || []).forEach(channel => {
            const coords = channel.coordinates || {};
            const x = coords.surfmm_x ?? coords.mm_x ?? coords.x;
            const y = coords.surfmm_y ?? coords.mm_y ?? coords.y;
            const z = coords.surfmm_z ?? coords.mm_z ?? coords.z;
            if (!Number.isFinite(Number.parseFloat(x)) ||
                !Number.isFinite(Number.parseFloat(y)) ||
                !Number.isFinite(Number.parseFloat(z))) {
                return;
            }

            points.push({
                patientid: patientZScoreData.patient_id,
                labels: channel.channel,
                channel: channel.channel,
                roi: channel.roi || region.region_name,
                region_name: region.region_name,
                surfmm_x: x,
                surfmm_y: y,
                surfmm_z: z,
                mm_x: coords.mm_x ?? x,
                mm_y: coords.mm_y ?? y,
                mm_z: coords.mm_z ?? z,
                local_federated: true
            });
        });
    });

    return points;
};

const getPointZScore = (point, zScoreLookup, feature) => {
    if (!feature || !zScoreLookup) {
        return null;
    }

    const channelName = contactKey(point.labels || point.channel || '');
    const regionName = point.roi || '';
    const channelRegionKey = `${channelName}|${regionName}`;
    if (zScoreLookup.byChannelRegion.has(channelRegionKey)) {
        return zScoreLookup.byChannelRegion.get(channelRegionKey);
    }

    const coordinateKeys = [
        coordinateKey(point.surfmm_x, point.surfmm_y, point.surfmm_z),
        coordinateKey(point.mm_x, point.mm_y, point.mm_z)
    ];
    for (const key of coordinateKeys) {
        if (zScoreLookup.byCoordinate.has(key)) {
            return zScoreLookup.byCoordinate.get(key);
        }
    }

    return null;
};

// Electrode contacts without a directional z-score (plain locations, near-zero
// deviations, or unscored) render near-white so they read as neutral positions
// rather than positive deviations, which are red.
const ELECTRODE_NEUTRAL_COLOR = 0xF7F7F7;

const CLINICAL_LABEL_STYLES = {
    seizure_onset: { color: 0xFF2D95, haloColor: 0xFFFFFF, size: 4.8, priority: 5 },
    early_spread: { color: 0xFFB000, haloColor: 0x2B1600, size: 4.2, priority: 4 },
    interictal_spikes: { color: 0x00D7FF, haloColor: 0x001F36, size: 3.7, priority: 3 },
    brain_lesions: { color: 0xB66DFF, haloColor: 0x1E0036, size: 3.5, priority: 2 },
    epileptic: { color: 0x4DFF88, haloColor: 0x003A1C, size: 3.2, priority: 1 }
};

const buildClinicalOverlayLookup = (clinicalOverlayData) => {
    const lookup = {
        byContactRegion: new Map(),
        byCoordinate: new Map()
    };

    (clinicalOverlayData?.patients || []).forEach(patient => {
        (patient.contacts || []).forEach(contact => {
            const style = CLINICAL_LABEL_STYLES[contact.primary_label];
            if (!style) {
                return;
            }

            const overlay = {
                ...contact,
                style
            };
            const contactName = contactKey(contact.contact);
            const regionName = contact.roi || '';
            const contactRegionKey = `${contactName}|${regionName}`;
            const previousContactOverlay = lookup.byContactRegion.get(contactRegionKey);
            if (!previousContactOverlay || overlay.style.priority > previousContactOverlay.style.priority) {
                lookup.byContactRegion.set(contactRegionKey, overlay);
            }

            const coords = contact.coordinates || {};
            [
                coordinateKey(coords.surfmm_x, coords.surfmm_y, coords.surfmm_z),
                coordinateKey(coords.mm_x, coords.mm_y, coords.mm_z)
            ].forEach(key => {
                if (key !== '||') {
                    const previous = lookup.byCoordinate.get(key);
                    if (!previous || overlay.style.priority > previous.style.priority) {
                        lookup.byCoordinate.set(key, overlay);
                    }
                }
            });
        });
    });

    return lookup;
};

const getPointClinicalOverlay = (point, clinicalLookup) => {
    if (!clinicalLookup) {
        return null;
    }

    const contactName = contactKey(point.labels || point.channel || '');
    const regionName = point.roi || '';
    const contactRegionKey = `${contactName}|${regionName}`;
    if (clinicalLookup.byContactRegion.has(contactRegionKey)) {
        return clinicalLookup.byContactRegion.get(contactRegionKey);
    }

    const coordinateKeys = [
        coordinateKey(point.surfmm_x, point.surfmm_y, point.surfmm_z),
        coordinateKey(point.mm_x, point.mm_y, point.mm_z)
    ];
    for (const key of coordinateKeys) {
        if (clinicalLookup.byCoordinate.has(key)) {
            return clinicalLookup.byCoordinate.get(key);
        }
    }

    return null;
};

// Reuse the electrode z-score lookup for a clinical contact so its ring can be
// filtered by the same |z| threshold as the dots. The contact stores its coords
// nested under `coordinates`; reshape it into the point form getPointZScore expects.
const getContactZScore = (contact, zScoreLookup, feature) => {
    const coords = contact?.coordinates || {};
    return getPointZScore({
        labels: contact?.contact,
        roi: contact?.roi,
        surfmm_x: coords.surfmm_x,
        surfmm_y: coords.surfmm_y,
        surfmm_z: coords.surfmm_z,
        mm_x: coords.mm_x,
        mm_y: coords.mm_y,
        mm_z: coords.mm_z
    }, zScoreLookup, feature);
};

// Mirror the electrode threshold rule (see plotChunk) for a clinical ring: under
// threshold mode a contact must have a z-score whose magnitude clears the slider,
// otherwise the ring is hidden just like its dot.
const clinicalOverlayPassesThreshold = (zScore, settings) => {
    if (!settings?.thresholdMode) {
        return true;
    }
    const threshold = Number(settings.threshold ?? 0);
    if (zScore === null || zScore === undefined) {
        return false;
    }
    if (!Number.isFinite(threshold)) {
        return true;
    }
    return Math.abs(Number(zScore)) >= threshold;
};

const clinicalOverlayKey = (patientId, overlay) => {
    const coords = overlay?.coordinates || {};
    return [
        patientId,
        contactKey(overlay?.contact || ''),
        coordinateKey(coords.surfmm_x, coords.surfmm_y, coords.surfmm_z),
        coordinateKey(coords.mm_x, coords.mm_y, coords.mm_z)
    ].join('|');
};

const drawClinicalOverlayContact = (viewer, patientId, overlay, globalPointsVisible) => {
    const coords = overlay?.coordinates || {};
    const x = parseFloat(coords.surfmm_x ?? coords.mm_x);
    const y = parseFloat(coords.surfmm_y ?? coords.mm_y);
    const z = parseFloat(coords.surfmm_z ?? coords.mm_z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        return null;
    }

    const marker = drawClinicalOverlayMarker(viewer, x, y, z, overlay.style);
    if (!marker) {
        return null;
    }

    marker.name = `clinical-${patientId}-points`;
    marker.visible = globalPointsVisible;
    marker.userData = {
        patientId,
        contact: overlay.contact,
        roi: overlay.roi,
        regionName: overlay.region_name || overlay.roi,
        lobe: overlay.lobe,
        clinicalLabel: overlay.primary_label,
        clinicalLabelDisplay: overlay.primary_label_display,
        labels: overlay.labels
    };
    return marker;
};

const isPatientPointObjectName = (name) => (
    Boolean(name) &&
    name.includes('-points') &&
    (name.includes('patient-') || name.includes('clinical-'))
);

const getModelAdjustedPosition = (viewer, x, y, z) => {
    const position = new THREE.Vector3(x, y, z);
    const offset = viewer?.model?.userData?.model_center_offset;
    if (offset) {
        position.x -= offset.x;
        position.y -= offset.y;
        position.z -= offset.z;
    }
    return position;
};

// BrainBrowser bundles its own THREE (r69) that owns viewer.model. Objects added
// to the model must be built with that same instance — a Group/Mesh from the app's
// npm three is rejected by viewer.model.add as a foreign (non-Object3D) type.
const getViewerThree = (viewer) => (
    viewer?.THREE ||
    (typeof window !== 'undefined' && window.BrainBrowser?.SurfaceViewer?.THREE) ||
    THREE
);

const getClinicalMarkerMaterial = (viewer, color, opacity = 1) => {
    const ViewerTHREE = getViewerThree(viewer);
    if (!viewer._clinicalMarkerMaterialCache) {
        viewer._clinicalMarkerMaterialCache = {};
    }

    const cacheKey = `${color}-${opacity}`;
    if (!viewer._clinicalMarkerMaterialCache[cacheKey]) {
        viewer._clinicalMarkerMaterialCache[cacheKey] = new ViewerTHREE.MeshBasicMaterial({
            color,
            // Always render clinical markers on top of the cortex so they stay
            // visible from any angle. With depthTest the rings get occluded by
            // the opaque brain surface once a contact rotates to the far side;
            // transparent:true also moves them into the post-opaque render pass.
            transparent: true,
            opacity,
            depthTest: false,
            depthWrite: false,
            side: ViewerTHREE.DoubleSide
        });
    }

    return viewer._clinicalMarkerMaterialCache[cacheKey];
};

const drawClinicalOverlayMarker = (viewer, x, y, z, style) => {
    if (!viewer?.model) {
        return null;
    }

    const ViewerTHREE = getViewerThree(viewer);
    const radius = style.size;
    const tubeRadius = Math.max(radius * 0.075, 0.18);
    const ringGeometry = new ViewerTHREE.TorusGeometry(radius, tubeRadius, 8, 32);
    const mainMaterial = getClinicalMarkerMaterial(viewer, style.color, 1);
    const haloMaterial = getClinicalMarkerMaterial(viewer, style.haloColor || 0xFFFFFF, 0.55);
    const group = new ViewerTHREE.Group();

    const xyRing = new ViewerTHREE.Mesh(ringGeometry, mainMaterial);
    const yzRing = new ViewerTHREE.Mesh(ringGeometry, mainMaterial);
    yzRing.rotation.y = Math.PI / 2;

    const haloGeometry = new ViewerTHREE.TorusGeometry(radius * 1.25, tubeRadius * 0.75, 8, 32);
    const haloRing = new ViewerTHREE.Mesh(haloGeometry, haloMaterial);

    group.add(haloRing);
    group.add(xyRing);
    group.add(yzRing);
    // Draw after the cortex so the always-on-top markers layer correctly even
    // when the brain itself is rendered with reduced opacity.
    [haloRing, xyRing, yzRing].forEach((mesh) => { mesh.renderOrder = 999; });
    group.position.copy(getModelAdjustedPosition(viewer, x, y, z));
    group.userData.markerType = 'clinical-overlay';
    viewer.model.add(group);
    viewer.updated = true;

    return group;
};

export const usePatientData = (viewer) => {
    const [error, setError] = useState({});
    const [loading, setLoading] = useState(false);
    const plotRunIdRef = useRef(0);
    const activePlotSignatureRef = useRef(null);

    const removePatientPointObjects = useCallback((matcher) => {
        if (!viewer?.model) {
            return;
        }

        const objectsToRemove = [];
        viewer.model.traverse(object => {
            if (object?.name && matcher(object.name)) {
                objectsToRemove.push(object);
            }
        });

        objectsToRemove.forEach(object => {
            if (object.parent && typeof object.parent.remove === 'function') {
                object.parent.remove(object);
            } else if (typeof viewer.model.remove === 'function') {
                viewer.model.remove(object);
            }
        });
    }, [viewer]);

    // Helper function to get z-score color based on direction and magnitude
    const getZScoreColor = (zScore, colorByDirection) => {
        const magnitude = Math.abs(zScore);

        if (!colorByDirection) {
            return ELECTRODE_NEUTRAL_COLOR;
        }

        if (magnitude < 0.25) {
            return ELECTRODE_NEUTRAL_COLOR;
        }

        const intensity = Math.min(magnitude / 3, 1); // Cap at 3 standard deviations

        if (zScore > 0) {
            const red = 255;
            const green = 140 - Math.floor(120 * intensity);
            const blue = 140 - Math.floor(120 * intensity);
            return (red << 16) | (green << 8) | blue;
        }

        const red = 55 - Math.floor(45 * intensity);
        const green = 155 - Math.floor(115 * intensity);
        const blue = 255;
        return (red << 16) | (green << 8) | blue;
    };

    // Helper function to get electrode size based on z-score magnitude
    const getElectrodeSize = (zScore, scaleByMagnitude, baseSize = 1) => {
        if (!scaleByMagnitude) {
            return baseSize;
        }

        const magnitude = Math.abs(zScore);
        const scaleFactor = 0.5 + (Math.min(magnitude / 3, 1) * 2.5);
        return baseSize * scaleFactor;
    };

    // Batch fetch and plot multiple patients efficiently
    const fetchAndPlotPatientsBatch = useCallback(async (patientIds, zScoreVisualizationSettings = null, patientZScores = null, clinicalOverlayData = null, { force = false } = {}) => {
        if (!viewer || !patientIds || patientIds.length === 0) return;

        const normalizedPatientIds = [...new Set(patientIds)].sort();
        const plotSignature = JSON.stringify({
            patients: normalizedPatientIds,
            feature: zScoreVisualizationSettings?.feature || '',
            enabled: Boolean(zScoreVisualizationSettings?.enabled),
            colorByDirection: Boolean(zScoreVisualizationSettings?.colorByDirection),
            scaleByMagnitude: Boolean(zScoreVisualizationSettings?.scaleByMagnitude),
            thresholdMode: Boolean(zScoreVisualizationSettings?.thresholdMode),
            threshold: Number(zScoreVisualizationSettings?.threshold ?? 0),
            clinicalOverlay: Boolean(zScoreVisualizationSettings?.showClinicalOverlay),
            zScorePatients: Array.isArray(patientZScores)
                ? patientZScores.map(patient => normalizePatientId(patient.patient_id)).sort()
                : [],
            clinicalPatients: Array.isArray(clinicalOverlayData?.patients)
                ? clinicalOverlayData.patients.map(patient => normalizePatientId(patient.patient_id)).sort()
                : []
        });

        if (!force && activePlotSignatureRef.current === plotSignature) {
            return;
        }
        activePlotSignatureRef.current = plotSignature;

        const plotRunId = plotRunIdRef.current + 1;
        plotRunIdRef.current = plotRunId;
        setLoading(true);

        // Clear errors for all patients
        setError(prev => {
            const newError = { ...prev };
            patientIds.forEach(id => { newError[id] = null; });
            return newError;
        });

        try {
            // Check cache for each patient. Local/federated uploads already carry
            // coordinates in patientZScores, so they should never be fetched from the backend.
            const dataByPatient = {};
            const patientsToFetch = [];
            const zScorePatientById = new Map();
            if (Array.isArray(patientZScores)) {
                patientZScores.forEach(patient => {
                    zScorePatientById.set(normalizePatientId(patient.patient_id), patient);
                });
            }

            patientIds.forEach(patientId => {
                const patientZScoreData = zScorePatientById.get(normalizePatientId(patientId));
                if (patientZScoreData?.local_federated) {
                    dataByPatient[patientId] = flattenLocalPatientZScorePoints(patientZScoreData);
                    return;
                }

                const cachedData = getCachedPatientData(patientId);
                if (cachedData) {
                    dataByPatient[patientId] = cachedData;
                } else {
                    patientsToFetch.push(patientId);
                }
            });

            if (patientsToFetch.length > 0) {
                const params = patientsToFetch.map(id => `patientids=${encodeURIComponent(id)}`).join('&');
                const response = await apiFetch(`/api/patients?${params}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch patient data');
                }
                const fetchedData = await response.json();

                fetchedData.forEach(point => {
                    const pid = point.patientid;
                    if (!dataByPatient[pid]) {
                        dataByPatient[pid] = [];
                    }
                    dataByPatient[pid].push(point);
                });

                patientsToFetch.forEach(patientId => {
                    if (dataByPatient[patientId]) {
                        setCachedPatientData(patientId, dataByPatient[patientId]);
                    }
                });
            }

            // A newer plot run, or a clear/deselect, superseded this batch while we
            // were fetching. Abort before mutating the scene so we don't re-add
            // points for a stale (e.g. just-deselected) selection.
            if (plotRunIdRef.current !== plotRunId) {
                setLoading(false);
                return;
            }

            // Remove existing patient points once and capture visibility preference
            const globalPointsVisible = document.body.getAttribute('data-points-visible') !== 'false';
            const patientPointNames = new Set(
                patientIds.flatMap(id => [`patient-${id}-points`, `clinical-${id}-points`])
            );
            removePatientPointObjects(name => patientPointNames.has(name));

            // Prepare configs to avoid recalculating lookups inside the render loop
            const configs = patientIds.map(patientId => {
                const data = dataByPatient[patientId] || [];
                let patientZScoreData = null;
                if (patientZScores && zScoreVisualizationSettings?.enabled && zScoreVisualizationSettings?.feature) {
                    patientZScoreData = patientZScores.find(p => (
                        normalizePatientId(p.patient_id) === normalizePatientId(patientId)
                    )) || null;
                }
                return {
                    patientId,
                    data,
                    patientZScoreData,
                    zScoreLookup: buildZScoreLookup(patientZScoreData, zScoreVisualizationSettings?.feature),
                    clinicalLookup: zScoreVisualizationSettings?.showClinicalOverlay
                        ? buildClinicalOverlayLookup(clinicalOverlayData)
                        : null,
                    clinicalContacts: zScoreVisualizationSettings?.showClinicalOverlay
                        ? (clinicalOverlayData?.patients || [])
                            .find(patient => normalizePatientId(patient.patient_id) === normalizePatientId(patientId))
                            ?.contacts || []
                        : []
                };
            });

            const plottedClinicalOverlayKeys = new Set();
            configs.forEach(({ patientId, clinicalContacts, patientZScoreData, zScoreLookup }) => {
                // When the z-score threshold slider is active, filter the clinical
                // rings by the same |z| cutoff as the electrode dots they annotate.
                const filterByThreshold = Boolean(
                    patientZScoreData &&
                    zScoreVisualizationSettings?.enabled &&
                    zScoreVisualizationSettings?.feature &&
                    zScoreVisualizationSettings?.thresholdMode
                );
                clinicalContacts.forEach(contact => {
                    const style = CLINICAL_LABEL_STYLES[contact.primary_label];
                    if (!style) {
                        return;
                    }
                    const overlay = { ...contact, style };
                    const key = clinicalOverlayKey(patientId, overlay);
                    if (plottedClinicalOverlayKeys.has(key)) {
                        return;
                    }
                    if (filterByThreshold) {
                        const contactZScore = getContactZScore(contact, zScoreLookup, zScoreVisualizationSettings.feature);
                        if (!clinicalOverlayPassesThreshold(contactZScore, zScoreVisualizationSettings)) {
                            // Mark handled so the per-point fallback below skips it too.
                            plottedClinicalOverlayKeys.add(key);
                            return;
                        }
                    }
                    const marker = drawClinicalOverlayContact(viewer, patientId, overlay, globalPointsVisible);
                    if (marker) {
                        plottedClinicalOverlayKeys.add(key);
                    }
                });
            });

            const maxPointsPerFrame = 400; // Number of points drawn per animation frame
            let patientIndex = 0;
            let pointIndex = 0;

            const plotChunk = () => {
                if (plotRunIdRef.current !== plotRunId) {
                    return;
                }

                if (!viewer || !viewer.model || patientIndex >= configs.length) {
                    if (plotRunIdRef.current === plotRunId) {
                        setLoading(false);
                    }
                    return;
                }

                let processedPoints = 0;

                while (patientIndex < configs.length && processedPoints < maxPointsPerFrame) {
                    const { patientId, data, patientZScoreData, zScoreLookup, clinicalLookup } = configs[patientIndex];

                    while (pointIndex < data.length && processedPoints < maxPointsPerFrame) {
                        const point = data[pointIndex];
                        pointIndex++;
                        processedPoints++;

                        const x = parseFloat(point.surfmm_x);
                        const y = parseFloat(point.surfmm_y);
                        const z = parseFloat(point.surfmm_z);
                        if (isNaN(x) || isNaN(y) || isNaN(z)) continue;

                        let color = ELECTRODE_NEUTRAL_COLOR;
                        let size = 1;
                        let shouldDrawElectrode = true;

                        if (patientZScoreData && zScoreVisualizationSettings?.enabled && zScoreVisualizationSettings?.feature) {
                            const electrodeZScore = getPointZScore(
                                point,
                                zScoreLookup,
                                zScoreVisualizationSettings.feature
                            );

                            if (electrodeZScore !== null && electrodeZScore !== undefined) {
                                const threshold = Number(zScoreVisualizationSettings.threshold ?? 0);
                                if (zScoreVisualizationSettings.thresholdMode && Number.isFinite(threshold)) {
                                    shouldDrawElectrode = Math.abs(Number(electrodeZScore)) >= threshold;
                                }
                                color = getZScoreColor(electrodeZScore, zScoreVisualizationSettings.colorByDirection);
                                size = getElectrodeSize(electrodeZScore, zScoreVisualizationSettings.scaleByMagnitude);
                            } else if (zScoreVisualizationSettings.thresholdMode) {
                                shouldDrawElectrode = false;
                            }
                        }

                        const dot = shouldDrawElectrode ? viewer.drawDot(x, y, z, size, color) : null;
                        if (dot) {
                            dot.name = `patient-${patientId}-points`;
                            dot.visible = globalPointsVisible;
                            if (patientZScoreData) {
                                dot.userData = dot.userData || {};
                                dot.userData.patientId = patientId;
                                dot.userData.coordinates = { x, y, z };
                                dot.userData.zScore = getPointZScore(
                                    point,
                                    zScoreLookup,
                                    zScoreVisualizationSettings?.feature
                                );
                                dot.userData.feature = zScoreVisualizationSettings?.feature;
                            }
                        }

                        // Skip the ring when the threshold hid its electrode so
                        // clinical overlays filter in lockstep with the z-score dots.
                        const clinicalOverlay = getPointClinicalOverlay(point, clinicalLookup);
                        if (clinicalOverlay && shouldDrawElectrode) {
                            const overlayKey = clinicalOverlayKey(patientId, clinicalOverlay);
                            const overlayMarker = plottedClinicalOverlayKeys.has(overlayKey)
                                ? null
                                : drawClinicalOverlayMarker(viewer, x, y, z, clinicalOverlay.style);
                            if (overlayMarker) {
                                plottedClinicalOverlayKeys.add(overlayKey);
                                overlayMarker.name = `clinical-${patientId}-points`;
                                overlayMarker.visible = globalPointsVisible;
                                overlayMarker.userData = {
                                    patientId,
                                    contact: clinicalOverlay.contact,
                                    roi: clinicalOverlay.roi,
                                    regionName: clinicalOverlay.region_name || clinicalOverlay.roi,
                                    lobe: clinicalOverlay.lobe,
                                    clinicalLabel: clinicalOverlay.primary_label,
                                    clinicalLabelDisplay: clinicalOverlay.primary_label_display,
                                    labels: clinicalOverlay.labels
                                };
                            }
                        }
                    }

                    if (pointIndex >= data.length) {
                        patientIndex++;
                        pointIndex = 0;
                    }
                }

                viewer.render();

                if (patientIndex < configs.length) {
                    requestAnimationFrame(plotChunk);
                } else {
                    if (plotRunIdRef.current === plotRunId) {
                        setLoading(false);
                    }
                }
            };

            requestAnimationFrame(plotChunk);

        } catch (err) {
            setError(prev => {
                const newError = { ...prev };
                patientIds.forEach(id => { newError[id] = err.message; });
                return newError;
            });
            if (plotRunIdRef.current === plotRunId) {
                setLoading(false);
            }
        }
    }, [viewer, removePatientPointObjects]);

    const fetchAndPlotPatient = useCallback(async (patientId, zScoreVisualizationSettings = null, patientZScores = null, clinicalOverlayData = null) => {
        // Use batch function for single patient for consistency
        return fetchAndPlotPatientsBatch([patientId], zScoreVisualizationSettings, patientZScores, clinicalOverlayData);
    }, [fetchAndPlotPatientsBatch]);

    const clearPatientData = useCallback((patientId) => {
        if (!viewer) return;

        plotRunIdRef.current += 1;
        activePlotSignatureRef.current = null;
        // Remove all points for this patient
        removePatientPointObjects(name => name === `patient-${patientId}-points` || name === `clinical-${patientId}-points`);
        viewer.render();

        // Note: We don't clear the cache here, so data can be reused if patient is re-selected
        // To clear cache, use clearPatientDataCache(patientId) if needed
    }, [viewer, removePatientPointObjects]);

    const clearAllPatientData = useCallback(() => {
        if (!viewer) return;

        plotRunIdRef.current += 1;
        activePlotSignatureRef.current = null;
        // Remove all patient points (any object with a name that includes 'patient-' and '-points')
        removePatientPointObjects(isPatientPointObjectName);
        viewer.render();
    }, [viewer, removePatientPointObjects]);

    const updatePatientVisualization = useCallback((patientIds, zScoreVisualizationSettings, patientZScores, clinicalOverlayData = null, options = {}) => {
        if (!viewer) return;
        const ids = Array.isArray(patientIds) ? patientIds : [];

        // Reconcile the scene to exactly this selection: drop any plotted
        // patient/clinical markers that are no longer selected. With an empty
        // selection this clears everything.
        const keep = new Set(ids.flatMap(id => [`patient-${id}-points`, `clinical-${id}-points`]));
        removePatientPointObjects(name => isPatientPointObjectName(name) && !keep.has(name));

        if (ids.length === 0) {
            // Nothing selected: cancel any in-flight plot so it can't re-add points
            // after we've cleared, and reset the dedup signature.
            plotRunIdRef.current += 1;
            activePlotSignatureRef.current = null;
            viewer.render?.();
            return;
        }

        fetchAndPlotPatientsBatch(ids, zScoreVisualizationSettings, patientZScores, clinicalOverlayData, options);
    }, [viewer, fetchAndPlotPatientsBatch, removePatientPointObjects]);

    return {
        fetchAndPlotPatient,
        fetchAndPlotPatientsBatch,
        clearPatientData,
        clearAllPatientData,
        updatePatientVisualization,
        error,
        loading
    };
};
